import React, { useEffect, useRef, useState, useCallback } from "react";
import { Button } from "@/components/ui/button";
import { Play, RotateCcw } from "lucide-react";

const WORDS = ["ARCADE", "JOYSTICK", "PIXEL", "CONSOLE", "AVATAR", "POWERUP", "RESPAWN", "CHECKPOINT", "MULTIPLAYER", "LEADERBOARD", "ACHIEVEMENT", "SPEEDRUN", "SANDBOX", "ROGUELIKE", "PLATFORMER"];
const MAX_WRONG = 6;
const ALPHABET = "ABCDEFGHIJKLMNOPQRSTUVWXYZ".split("");

export function Hangman({ onScore }) {
  const [word, setWord] = useState("");
  const [guessed, setGuessed] = useState(new Set());
  const [wrong, setWrong] = useState(0);
  const [status, setStatus] = useState("idle"); // idle | playing | won | lost
  const reported = useRef(false);

  const start = () => {
    setWord(WORDS[Math.floor(Math.random() * WORDS.length)]);
    setGuessed(new Set()); setWrong(0); setStatus("playing"); reported.current = false;
  };

  const guess = useCallback((letter) => {
    if (status !== "playing" || guessed.has(letter)) return;
    const ng = new Set(guessed); ng.add(letter); setGuessed(ng);
    if (!word.includes(letter)) {
      const w = wrong + 1; setWrong(w);
      if (w >= MAX_WRONG) setStatus("lost");
    } else if (word.split("").every((c) => ng.has(c))) {
      setStatus("won");
    }
  }, [status, guessed, word, wrong]);

  useEffect(() => {
    const h = (e) => { const k = e.key.toUpperCase(); if (/^[A-Z]$/.test(k)) guess(k); };
    window.addEventListener("keydown", h);
    return () => window.removeEventListener("keydown", h);
  }, [guess]);

  useEffect(() => {
    if ((status === "won" || status === "lost") && !reported.current) {
      reported.current = true;
      onScore?.(status === "won" ? (MAX_WRONG - wrong) * 100 + 100 : 0);
    }
  }, [status, wrong, onScore]);

  return (
    <div className="flex flex-col items-center w-full max-w-lg" data-testid="hangman-game">
      <div className="flex items-center justify-between w-full mb-4 font-mono text-sm">
        <span className="text-neon-pink">❌ {wrong}/{MAX_WRONG}</span>
        <span className="text-neon-cyan">Guess the gaming word!</span>
      </div>

      <div className="text-5xl mb-6" data-testid="hangman-figure">
        {["😀", "🙂", "😐", "😟", "😨", "😰", "💀"][wrong]}
      </div>

      {status === "idle" ? (
        <Button onClick={start} data-testid="hangman-start" className="bg-neon-cyan text-black rounded-none font-bold uppercase tracking-widest h-12 px-8">
          <Play className="w-4 h-4 mr-2 fill-black" /> Start
        </Button>
      ) : (
        <>
          <div className="flex flex-wrap justify-center gap-2 mb-6" data-testid="hangman-word">
            {word.split("").map((c, i) => (
              <span key={i} className="w-8 h-10 border-b-4 border-zinc-600 flex items-center justify-center font-display font-black text-2xl"
                style={{ color: guessed.has(c) || status === "lost" ? (status === "lost" && !guessed.has(c) ? "#FF003C" : "#00F0FF") : "transparent" }}>
                {guessed.has(c) || status === "lost" ? c : "_"}
              </span>
            ))}
          </div>

          {status === "playing" ? (
            <div className="grid grid-cols-7 gap-1.5 w-full">
              {ALPHABET.map((l) => {
                const used = guessed.has(l);
                const right = used && word.includes(l);
                return (
                  <button key={l} onClick={() => guess(l)} data-testid={`hangman-key-${l}`} disabled={used}
                    className="aspect-square rounded font-bold text-sm transition-colors"
                    style={{ background: used ? (right ? "#39FF14" : "#27272a") : "#3f3f46", color: right ? "#000" : used ? "#52525b" : "#fff" }}>
                    {l}
                  </button>
                );
              })}
            </div>
          ) : (
            <div className="text-center">
              <p className={`font-display font-black text-2xl uppercase ${status === "won" ? "text-neon-lime" : "text-neon-pink"}`} data-testid="hangman-result">
                {status === "won" ? "You guessed it! 🎉" : `Game over — it was ${word}`}
              </p>
              <Button onClick={start} className="mt-4 bg-neon-cyan text-black rounded-none font-bold uppercase">
                <RotateCcw className="w-4 h-4 mr-1" /> New Word
              </Button>
            </div>
          )}
        </>
      )}
    </div>
  );
}
